import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, ChevronRight } from 'lucide-react';
import { salesApi, formatMoneyShort, serverError } from '../../lib/salesApi';
import ClientProfileDrawer from '../../components/sales/ClientProfileDrawer';

const CY = '#90E0EF';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; }

export default function SalesClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesMissing, setTablesMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  function load(s) {
    setLoading(true);
    salesApi.clients(s || undefined)
      .then((d) => { setClients(d.clients || []); setTablesMissing(false); })
      .catch((e) => { if (e?.response?.status === 503) setTablesMissing(true); else toast.error(serverError(e)); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(''); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  if (tablesMissing) {
    return <div className="border border-[#2a2416] bg-[#141207] px-6 py-8 text-center">
      <p className="font-outfit text-lg text-[#e4c07a]">Sales tables not initialised</p>
      <p className="text-xs font-mono text-[#aa9] mt-2">Run the SQL migration, then reload.</p>
    </div>;
  }

  return (
    <div>
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-3 w-4 h-4 text-[#555]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search collectors by name or email" data-testid="client-search"
          className="w-full bg-[#0d0d0d] border border-[#222] text-[#f0f0f0] text-sm font-mono pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#555]" />
      </div>

      <div className="border border-[#1a1a1a]" data-testid="clients-table">
        <div className="hidden md:grid grid-cols-[1fr_1fr_120px_100px_130px_30px] gap-3 px-4 py-2.5 border-b border-[#1a1a1a] bg-[#0a0a0a] text-[9px] font-mono uppercase tracking-[0.14em] text-[#666]">
          <span>Name</span><span>Email</span><span className="text-right">Total</span><span className="text-right">Purchases</span><span className="text-right">Last</span><span />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: CY }} /></div>
        ) : clients.length === 0 ? (
          <p className="px-4 py-8 text-center text-xs font-mono text-[#555]">No collectors yet.</p>
        ) : clients.map((c) => (
          <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`client-row-${c.id}`} style={{ cursor: 'pointer' }}
            className="w-full text-left grid grid-cols-[1fr_60px] md:grid-cols-[1fr_1fr_120px_100px_130px_30px] gap-3 px-4 py-3 border-b border-[#141414] items-center hover:bg-[rgba(144,224,239,0.03)] transition-colors">
            <span className="text-sm text-[#e8e8e8] truncate">{c.name || 'Unnamed'}</span>
            <span className="text-xs font-mono text-[#888] truncate hidden md:block">{c.email}</span>
            <span className="font-mono text-sm text-white text-right">{formatMoneyShort(c.total_purchased_minor, c.default_currency)}</span>
            <span className="font-mono text-xs text-[#aaa] text-right hidden md:block">{c.purchase_count}</span>
            <span className="font-mono text-[10px] text-[#888] text-right hidden md:block">{fmtDate(c.last_purchase_at)}</span>
            <ChevronRight className="w-4 h-4 text-[#555] justify-self-end" />
          </button>
        ))}
      </div>

      <ClientProfileDrawer clientId={selected} open={!!selected} onClose={() => { setSelected(null); load(search); }} />
    </div>
  );
}
